import React, { useEffect, useState } from "react";
import { Check, Link2, Loader2, Search } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface LinkedRecord {
  type: "LEAD" | "CONTACT";
  id: string;
  label: string;
  sub: string;
  numberSaved: boolean;
}

interface Hit { type: "LEAD" | "CONTACT"; id: string; label: string; sub: string; owner: string }

const full = (a?: string | null, b?: string | null) => `${a || ""} ${b || ""}`.trim();
const ownerName = (o: any) => (o ? full(o.first_name, o.last_name) : "");

/**
 * "Who is this?": links a call from a number we don't know to a lead or contact the person can see, and can save the
 * caller's number on the record so the next call rings its owner. Used on the open call and in the Calls drawer.
 */
export function LinkCaller({ callId, onLinked, onSkip, skipLabel = "Not now" }: { callId: string; onLinked: (r: LinkedRecord) => void; onSkip?: () => void; skipLabel?: string }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [save, setSave] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return; }
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const qs = `search=${encodeURIComponent(term)}&page_size=5`;
        const [leads, contacts] = await Promise.all([
          api.get(`/api/Lead/List?${qs}`).then((r) => unwrapPage<any>(r).rows).catch(() => []),
          api.get(`/api/Contact/List?${qs}`).then((r) => unwrapPage<any>(r).rows).catch(() => []),
        ]);
        if (!live) return;
        setHits([
          ...leads.map((l: any): Hit => ({ type: "LEAD", id: l.id, label: full(l.first_name, l.last_name) || l.company_name || "Lead", sub: `Lead${l.company_name ? ` · ${l.company_name}` : ""}`, owner: ownerName(l.owner) })),
          ...contacts.map((c: any): Hit => ({ type: "CONTACT", id: c.id, label: full(c.first_name, c.last_name) || "Contact", sub: `Contact${c.account?.name || c.account_name ? ` · ${c.account?.name || c.account_name}` : ""}`, owner: ownerName(c.owner) })),
        ]);
      } finally {
        if (live) setBusy(false);
      }
    }, 280);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  const link = async (h: Hit) => {
    setLinking(h.id);
    setError(null);
    try {
      const r = unwrapObject<{ label: string; number_saved: string | null }>(await api.post(`/api/Call/Link/${callId}`, { related_type: h.type, related_id: h.id, save_number: save }));
      onLinked({ type: h.type, id: h.id, label: r.label || h.label, sub: h.sub, numberSaved: !!r.number_saved });
    } catch (err: any) {
      setError(err?.message || "Couldn't link the call. Try again.");
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="space-y-2.5">
      <p className="text-[12.5px] text-muted-foreground">Who is this? Link the call to a lead or contact.</p>
      <label className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm focus-within:ring-2 focus-within:ring-ring">
        {busy ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : <Search className="size-3.5 text-muted-foreground" />}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or company" aria-label="Search leads and contacts" className="min-w-0 flex-1 bg-transparent outline-none" autoComplete="off" />
      </label>
      {hits.length > 0 && (
        <ul className="divide-y rounded-md border" aria-label="Matches">
          {hits.map((h) => (
            <li key={`${h.type}${h.id}`} className="flex items-center gap-2 px-2.5 py-1.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold">{h.label}</span>
                <span className="block truncate text-[11.5px] text-muted-foreground">{h.sub}{h.owner ? ` · ${h.owner}` : ""}</span>
              </span>
              <Button type="button" size="sm" variant="outline" className="h-7 shrink-0" disabled={linking !== null} onClick={() => link(h)}>
                {linking === h.id ? <Loader2 className="size-3.5 animate-spin" /> : <Link2 className="size-3.5" />}
                Link
              </Button>
            </li>
          ))}
        </ul>
      )}
      {q.trim().length >= 2 && !busy && hits.length === 0 && <p className="text-xs text-muted-foreground">No lead or contact you can see matches “{q.trim()}”.</p>}
      <button type="button" onClick={() => setSave((v) => !v)} aria-pressed={save} className="flex items-center gap-2 text-[12.5px]">
        <span className={cn("flex size-4 items-center justify-center rounded border", save ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background")}>{save && <Check className="size-3" strokeWidth={3} />}</span>
        Save this number on the record
      </button>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      {onSkip && (
        <div className="flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onSkip}>{skipLabel}</Button>
        </div>
      )}
    </div>
  );
}
